import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import { LoadingButton } from '@mui/lab';
import { Backdrop, Box, Button, Typography } from '@mui/material';
import { useLazyGetUsersQuery } from 'src/common/apis/AdminApi';
import { useUserRole } from 'src/common/hooks/useUserRole';

import { ReactComponent as StarIcon } from '../../../common/assets/icons/star_icon.svg';
import { ReactComponent as StarIconBlue } from '../../../common/assets/icons/star_icon_blue.svg';
import { ReactComponent as UserIcon } from '../../../common/assets/icons/users_icon.svg';
import { ReactComponent as UserIconBlue } from '../../../common/assets/icons/users_icon_blue.svg';
import SearchBar from '../../../common/components/SearchBar';
import { User, UserRoles } from '../../../common/types/User';
import UserFilterMenuButton from '../../components/MenuButton/UserFilterMenuButton';
import UsersTable, { Order } from '../../components/UsersTable/UsersTable';
import {
  defaultSubscriptionFilters,
  defaultUserFilters,
  UserFilterOption,
} from '../../constants/FilterOptions';
import useFilteredUsers from '../../hooks/useFilteredUsers';
import { downloadUsersCSV } from '../../libs/CsvService';
import MenuOption from '../../types/MenuOption';

import '../AdminContent.scss';
import AddUserToOrganizationPopup from './components/AddUserToOrganizationPopup';
import CreateUserPopup from './components/CreateUserPopup';

const UsersScreen: React.FC = () => {
  const [subscriptionFilters, setSubscriptionFilters] = React.useState<
    MenuOption<UserFilterOption>[]
  >(defaultSubscriptionFilters);

  const rowsPerPage = 9;

  const [userFilters, setUserFilters] =
    React.useState<MenuOption<UserFilterOption>[]>(defaultUserFilters);

  const [searchText, setSearchText] = React.useState<string | undefined>(
    undefined
  );
  const [sortParams, setSortParams] = React.useState<{
    sortField?: string;
    direction?: string;
  }>({ sortField: 'lastLogin', direction: 'DESC' });

  const {
    filteredUsers: users,
    isLoading: usersIsLoading,
    total,
    page,
    setPage,
  } = useFilteredUsers(
    rowsPerPage,
    userFilters,
    subscriptionFilters,
    sortParams,
    searchText
  );

  const [usersDownloading, setUsersDownloading] = React.useState(false);
  const [addUserPopup, setAddUserPopup] = React.useState(false);
  const [createUserPopup, setCreateUserPopup] = React.useState(false);
  const { userRole } = useUserRole();

  const [fetchAllUsers] = useLazyGetUsersQuery();

  const updateFilterOption = (
    filters: MenuOption<UserFilterOption>[],
    option: MenuOption<UserFilterOption>,
    checked: boolean
  ) => {
    return filters.map((filter) => {
      if (filter.item === option.item) {
        filter.selected = checked;
      }
      return filter;
    });
  };

  const handleSubscriptionFilterSelection = (
    option: MenuOption<UserFilterOption>,
    checked: boolean
  ) => {
    const update = updateFilterOption(subscriptionFilters, option, checked);
    setSubscriptionFilters(update);
  };

  const handleUserFilterSelection = (
    option: MenuOption<UserFilterOption>,
    checked: boolean
  ) => {
    const update = updateFilterOption(userFilters, option, checked);
    setUserFilters(update);
  };

  const handleClear = () => {
    setSearchText(undefined);
  };

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const handleDownload = async () => {
    setUsersDownloading(true);
    const response = await fetchAllUsers(
      {
        page: 1,
        limit: 100000000,
        search: '',
        sortField: 'email',
        direction: 'ASC',
      },
      false
    ).unwrap();
    await downloadUsersCSV(response.docs);
    setUsersDownloading(false);
  };

  const requestSort = (
    property: keyof Omit<User, 'lastKnownSubscription'>,
    order: Order
  ) => {
    let sortField: string = property;
    sortField =
      property === 'subscription'
        ? 'lastKnownSubscription'
        : (property as string);
    setSortParams({ sortField, direction: order.toUpperCase() });
  };

  return (
    <Box className='white-background min-height-100'>
      <Box className='content-wrapper'>
        <Box className='row-wrapper'>
          <SearchBar
            handleClear={handleClear}
            handleSearch={handleSearch}
            placeholder='Type the name or email'
          />
          <Box className='filters-row'>
            {userRole === UserRoles.SUPER_ADMIN && (
              <>
                <Typography color='text.secondary'>Filter by:</Typography>
                <UserFilterMenuButton
                  sx={{
                    marginRight: '16px',
                    marginLeft: '16px',
                  }}
                  IconOpen={UserIconBlue}
                  IconClosed={UserIcon}
                  title='Subscription'
                  options={subscriptionFilters}
                  handleSelection={handleSubscriptionFilterSelection}
                />
                <UserFilterMenuButton
                  IconOpen={StarIconBlue}
                  IconClosed={StarIcon}
                  title='Users'
                  options={userFilters}
                  handleSelection={handleUserFilterSelection}
                />
              </>
            )}

            <LoadingButton
              onClick={handleDownload}
              disabled={!users || users.length === 0}
              loading={usersDownloading}
              disableElevation
              color='primary'
              variant='contained'
              sx={{
                marginLeft: '40px',
                borderRadius: '40px',
                px: '48px',
                height: '50px',
              }}
            >
              <Typography color='white'>Download</Typography>
            </LoadingButton>

            <Button
              onClick={() => setAddUserPopup(true)}
              startIcon={<AddIcon htmlColor='#ffffff' />}
              disableElevation
              color='primary'
              variant='contained'
              sx={{
                borderRadius: '40px',
                px: '40px',
                height: '50px',
                marginLeft: '24px',
              }}
            >
              <Typography color='white'>Add User</Typography>
            </Button>

            {userRole === UserRoles.SUPER_ADMIN && (
              <Button
                onClick={() => setCreateUserPopup(true)}
                startIcon={<AddIcon htmlColor='#ffffff' />}
                disableElevation
                color='primary'
                variant='contained'
                sx={{
                  borderRadius: '40px',
                  px: '40px',
                  height: '50px',
                  marginLeft: '24px',
                }}
              >
                <Typography color='white'>Create User</Typography>
              </Button>
            )}
          </Box>
        </Box>
        <UsersTable
          requestSort={requestSort}
          rowsPerPage={rowsPerPage}
          page={page}
          setPage={setPage}
          total={total}
          users={users}
          isLoading={usersIsLoading}
        />
        <Backdrop
          open={addUserPopup}
          sx={{
            color: '#fff',
            zIndex: 99,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {addUserPopup && (
            <AddUserToOrganizationPopup
              onClose={() => setAddUserPopup(false)}
            />
          )}
        </Backdrop>
        <Backdrop
          open={createUserPopup}
          sx={{
            color: '#fff',
            zIndex: 99,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {createUserPopup && (
            <CreateUserPopup onClose={() => setCreateUserPopup(false)} />
          )}
        </Backdrop>
      </Box>
    </Box>
  );
};

export default UsersScreen;
